import React, { useState, useEffect, useRef, useCallback } from 'react';
import CreateMindModal from '../components/CreateMindModal';
import MindSettingsModal from '../components/MindSettingsModal';
import LoadingSpinner from '../components/LoadingSpinner';
import WelcomeModal from '../components/WelcomeModal';
import TrainingProgress from '../components/TrainingProgress';
import InterviewerSelector from '../components/InterviewerSelector';

import { useGlobalState } from '../context/GlobalState';
import { deusSocket, sendWithAuth } from '../utils/api';
import './Main.css';
import { getCurrentLevel } from '../config/levels';

import { useNavigate } from 'react-router-dom';

function Main() {
  const { user, setUser, minds, setMinds } = useGlobalState();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('favorites');
  const [trainingProgress, setTrainingProgress] = useState({}); // Track progress per mind
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [selectedInterviewers, setSelectedInterviewers] = useState('all'); // 'all' or array of interviewer keys

  const [isLoading, setIsLoading] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isCreateMindLoading, setIsCreateMindLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [activeMind, setActiveMind] = useState(null);
  const [activeChatId, setActiveChatId] = useState(null);
  const [isCreateMindOpen, setIsCreateMindOpen] = useState(false);
  const [isAIConversation, setIsAIConversation] = useState(false);
  const [activeProfile, setActiveProfile] = useState(null);
  const [isManageProfilesOpen, setIsManageProfilesOpen] = useState(false);
  const [editingMindId, setEditingMindId] = useState(null);
  const [editingMindName, setEditingMindName] = useState('');
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingChatName, setEditingChatName] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [copiedMessageIds, setCopiedMessageIds] = useState({});

  const sidebarRef = useRef(null);
  const resizerRef = useRef(null);
  const textareaRef = useRef(null);
  const currentRequestIdRef = useRef(null);

  const mindName = activeMind ? minds.find(mind => mind.id === activeMind)?.name : "";
  const activeMindData = activeMind ? minds.find(mind => mind.id === activeMind) : null;
  const activeChats = activeMindData?.chats || [];

  const copyConversation = () => {
    if (messages.length === 0) return;
    
    // Format the conversation
    let formattedText = '';
    messages.forEach(message => {
      const sender = message.is_user ? user.username || 'User' : mindName || 'Agent';
      formattedText += `${sender}: ${message.message}\n\n`;
    });
    
    // Copy to clipboard
    navigator.clipboard.writeText(formattedText)
      .then(() => {
        // Show a temporary success message
        const button = document.querySelector('.copy-conversation-icon');
        if (button) {
          // Store original content
          const originalContent = button.innerHTML;
          
          // Replace with checkmark icon
          button.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          `;
          
          // Add a success class
          button.classList.add('copy-success');
          
          // Reset after 2 seconds
          setTimeout(() => {
            button.innerHTML = originalContent;
            button.classList.remove('copy-success');
          }, 2000);
        }
      })
      .catch(err => {
        console.error('Failed to copy conversation: ', err);
      });
  };

  const copyMessage = (message, event) => {
    event.stopPropagation();
    
    const messageId = message.timestamp.toString(); // Use timestamp as a unique identifier
    
    navigator.clipboard.writeText(message.message)
      .then(() => {
        // Set this message as copied
        setCopiedMessageIds(prev => ({
          ...prev,
          [messageId]: true
        }));
        
        // Reset after 2 seconds
        setTimeout(() => {
          setCopiedMessageIds(prev => ({
            ...prev,
            [messageId]: false
          }));
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy message: ', err);
      });
  };

  const getInterviewerResponse = useCallback((explicitMindId = null) => {
    setIsTyping(true);
    const mindIdToUse = explicitMindId || activeMind;
    
    sendWithAuth('getInterviewerResponse', { 
      mindId: mindIdToUse, 
      selectedInterviewers: selectedInterviewers 
    }, (response) => {
      setIsTyping(false);
      if (response.success) {
        const aiMessage = {
          message: response.aiMessage,
          is_user: false,
          timestamp: new Date()
        };
        setMessages(prevMessages => [...prevMessages, aiMessage]);

        // Store the AI message in the database
        sendWithAuth('createChatMessage', {
          userId: user._id,
          mindId: mindIdToUse,
          message: response.aiMessage,
          model: response.model,
          isUser: false
        }, (storeResponse) => {
          if (!storeResponse.success) {
            console.error('Failed to store AI message:', storeResponse.error);
          }
        });
      } else {
        console.error('Failed to get AI response:', response.error);
      }
    });
  }, [activeMind, user, selectedInterviewers]);

  const getAgentResponse = useCallback((profileVersion, chatId) => {
    setIsTyping(true);
    
    // Use the explicitly provided chatId, fallback to activeChatId if none provided
    const targetChatId = chatId || activeChatId;
    
    // If no valid chat ID is available, log error and return
    if (isAIConversation && !targetChatId) {
      console.error('Cannot get agent response: No chat ID available');
      setIsTyping(false);
      return;
    }
    
    const payload = {
      mindId: activeMind,
      profileVersion,
      engine: 'gpt',
      chatId: targetChatId,
      userId: user._id
    };
    
    sendWithAuth('getAgentResponse', payload, (response) => {
      if (!response.success) {
        console.error('Failed to get AI response:', response.error);
        setIsTyping(false); // Only set false on error
        return;
      }

      // If a chat ID was returned but we're using a different one, prioritize the provided one
      const responseChatId = response.chatId || targetChatId;
      
      // If we didn't have a chat ID set in state but got one in the response, update it
      if (responseChatId && !activeChatId) {
        setActiveChatId(responseChatId);
        
        // Update minds state locally to include the new chat if needed
        setMinds(prevMinds => prevMinds.map(mind => {
          if (mind.id === activeMind) {
            // Check if this chat already exists in the mind
            const chatExists = mind.chats && mind.chats.some(chat => chat.id === responseChatId);
            
            // Only add if it doesn't exist
            if (!chatExists) {
              // Create a new chat object with the returned chatId
              const newChat = {
                id: responseChatId,
                title: `Chat ${new Date().toLocaleDateString()}`,
                created_at: new Date().toISOString()
              };
              return {
                ...mind,
                chats: [...(mind.chats || []), newChat]
              };
            }
          }
          return mind;
        }));
      }
      
      const aiMessage = {
        message: response.aiMessage,
        is_user: false,
        timestamp: new Date()
      };
      
      setMessages(prevMessages => [...prevMessages, aiMessage]);
      setIsTyping(false); // Set typing to false after we've processed the message

      // Store the AI message in the database using the correct chat ID
      sendWithAuth('createAIChatMessage', {
        userId: user._id,
        mindId: activeMind,
        chatId: responseChatId,
        profileVersion,
        message: response.aiMessage,
        model: response.model,
        isUser: false
      }, (storeResponse) => {
        if (!storeResponse.success) {
          console.error('Failed to store AI message:', storeResponse.error);
        }
      });
    });
  }, [activeMind, activeChatId, user, isAIConversation, setMinds, setActiveChatId]);

  const toggleConversationMode = () => {
    if (!activeProfile) {
      alert('Please create an agent profile to enable chat mode.');
      return;
    }
    
    // Clear messages immediately to avoid showing wrong mode's messages
    setMessages([]);
    
    const newConversationMode = !isAIConversation;
    
    // If turning on AI conversation mode
    if (newConversationMode && activeMind) {
      // Set the mode first
      setIsAIConversation(true);
      
      const currentMind = minds.find(mind => mind.id === activeMind);
      if (currentMind?.chats?.length > 0) {
        // Select the first chat - this triggers the message fetch
        setActiveChatId(currentMind.chats[0].id);
      } else {
        // Start typing animation right away
        setIsTyping(true);
        
        // No chats exist for this mind, create a new one
        sendWithAuth('createAIChat', {
          userId: user._id,
          mindId: activeMind
        }, (response) => {
          if (response.success) {
            // Extract the new chat ID
            const newChatId = response.chatId || response.chat.id;
            
            // Locally update the minds state with the new chat
            setMinds(prevMinds => prevMinds.map(mind => {
              if (mind.id === activeMind) {
                return {
                  ...mind,
                  chats: [response.chat, ...(mind.chats || [])]
                };
              }
              return mind;
            }));
            
            // Set the new chat as active - very important to do this before triggering the message
            setActiveChatId(newChatId);
          } else {
            console.error('Failed to create new chat:', response.error);
            setIsTyping(false);
          }
        });
      }
    } else {
      // Turning off AI conversation mode (going to training mode)
      setIsAIConversation(false);
    }
  };

  // Auto-respond when the last message is from the user
  useEffect(() => {
    // Skip if we don't have an active mind or if we're in AI mode but don't have a chat ID
    if (!activeMind || (isAIConversation && !activeChatId) || isChatLoading || messages.length === 0) {
      return;
    }
    
    // Get the last message
    const lastMessage = messages[messages.length - 1];
    
    // Only trigger a response if the last message is from the user
    if (lastMessage && lastMessage.is_user) {    
      // Add a small delay to ensure UI updates first
      const responseTimeout = setTimeout(() => {
        if (isAIConversation) {
          getAgentResponse(activeProfile, activeChatId);
        } else {
          getInterviewerResponse();
        }
      }, 300);
      
      return () => clearTimeout(responseTimeout);
    }
  }, [activeMind, activeChatId, isAIConversation, messages, isChatLoading, getInterviewerResponse, getAgentResponse, activeProfile]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await checkForUserToken();
      } catch (error) {
        console.error("Error fetching data:", error);
        navigate('/auth');
      }
    };

    fetchData();

    const handleConnect = () => {
      console.log(`Device connected to users server: ${user}`);
    };

    const handleErrorEvent = (err) => {
        alert(err.message);
        navigate('/auth');
    };

    const handleAutoSignInError = () => {
      navigate('/auth');
    };

    const handleUpdateResponse = async userData => {
      const userClone = { ...user, ...userData };
      setUser(userClone);
      setIsLoading(false);
    };

    const handleSignInAuto = async userPayload => {
      localStorage.setItem('token', userPayload.token);
      localStorage.setItem('user', userPayload._id.toString());
      setUser(userPayload);
      setIsLoading(false);
    };

    deusSocket.on('connect', handleConnect);
    deusSocket.on('error-event', handleErrorEvent);
    deusSocket.on('auto-sign-in-error', handleAutoSignInError);
    deusSocket.on('deliverUserSignInAuto', handleSignInAuto);
    deusSocket.on('deliverUpdateProfileRes', handleUpdateResponse);

    // Add a handler for email verification success
    const handleEmailVerified = (userData) => {
      // Update the user data in the context
      const userClone = { ...user, ...userData, email_verified: true };
      setUser(userClone);
    };
    
    deusSocket.on('emailVerified', handleEmailVerified);

    return () => {
      deusSocket.off('connect', handleConnect);
      deusSocket.off('error-event', handleErrorEvent);
      deusSocket.off('auto-sign-in-error', handleAutoSignInError);
      deusSocket.off('deliverUserSignInAuto', handleSignInAuto);
      deusSocket.off('deliverUpdateProfileRes', handleUpdateResponse);
      deusSocket.off('emailVerified', handleEmailVerified);
      
      // Clean up any profile creation notification when component unmounts
      const notification = document.getElementById('profile-creation-notification');
      if (notification) {
        notification.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      // Fetch user's minds
      sendWithAuth('getUserMinds', { 
        userId: user._id,
        sortBy: 'favorites' // Default sort for initial fetch
      });
    }

    const handleUserMinds = (minds) => {
      setMinds(minds);
      if (minds.length === 0) {
        setIsWelcomeModalOpen(true);
      } else if (!activeMind) {
        // Only set default selections if there is no active mind
        const firstMind = minds[0];
        const hasProfile = firstMind?.default_profile != null;
        
        setActiveMind(firstMind.id);
        setActiveProfile(firstMind.default_profile);
        setIsAIConversation(hasProfile);
        
        if (hasProfile && firstMind.chats && firstMind.chats.length > 0) {
          setActiveChatId(firstMind.chats[0].id);
        }
      }
    };

    const handleCreateMindResponse = (newMind) => {
      setMinds(prevMinds => [...prevMinds, newMind]);
      setIsAIConversation(false); // Ensure we're in training mode, not AI conversation mode
      setActiveMind(newMind.id);
      setActiveProfile(null);
      setIsCreateMindOpen(false);
      setIsCreateMindLoading(false);
    };

    const handleUpdateMindResponse = (updatedMind) => {
      setMinds(prevMinds => 
        prevMinds.map(mind => 
          mind.id === updatedMind.id ? { ...mind, ...updatedMind } : mind
        )
      );
    };

    deusSocket.on('deliverUserMinds', handleUserMinds);
    deusSocket.on('deliverCreateMind', handleCreateMindResponse);
    deusSocket.on('deliverUpdateMind', handleUpdateMindResponse);

    return () => {
      deusSocket.off('deliverUserMinds', handleUserMinds);
      deusSocket.off('deliverCreateMind', handleCreateMindResponse);
      deusSocket.off('deliverUpdateMind', handleUpdateMindResponse);
    };
  }, [user, setMinds]);

  const checkForUserToken = async () => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user');

    if (!token || !userId) {
      navigate('/auth');
      return;
    }

    const auth = { token, userId };
    const payload = { user: userId, token };

    sendWithAuth('signInAuto', payload, auth);
  }

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      const event = isAIConversation ? 'createAIChatMessage' : 'createChatMessage';

      const payload = {
        userId: user._id,
        mindId: activeMind,
        message: inputMessage,
        isUser: true
      };
      
      // Add chatId to payload if in AI conversation mode and a chat is selected
      if (isAIConversation && activeChatId) {
        payload.chatId = activeChatId;
      }
      
      // Create a local message object to immediately update the UI
      const newMessage = {
        message: inputMessage,
        is_user: true,
        timestamp: new Date()
      };
      
      // Store the current message for reference
      const currentInputMessage = inputMessage;
      
      // Clear input field immediately for better UX
      setInputMessage('');
      
      sendWithAuth(event, payload, (response) => {
        if (response.success) {
          // Add the message to the UI
          setMessages(prevMessages => [...prevMessages, newMessage]);
          
          // Update training progress if in training mode
          if (!isAIConversation) {
            const newProgress = (trainingProgress[activeMind] || 0) + 1;
            setTrainingProgress(prev => ({
              ...prev,
              [activeMind]: newProgress
            }));

            // Check if we've hit exactly a milestone
            const currentLevel = getCurrentLevel(newProgress);
               
            // Create profile when we hit the exact number required for current level
            if (newProgress === currentLevel.answersRequired && !isCreatingProfile) {
              setIsCreatingProfile(true);
              sendWithAuth('createCharacterProfile', { 
                mindId: activeMind, 
                optionType: 'deus v2.5',
                automated: true,
              }, (response) => {
                setIsCreatingProfile(false);
                if (response.success) {
                  setMinds(prevMinds => prevMinds.map(mind => {
                    if (mind.id === activeMind) {
                      return {
                        ...mind,
                        default_profile: response.profile.version
                      };
                    }
                    return mind;
                  }));
                  setActiveProfile(response.profile.version);
                  
                  // Check if fine-tuning failed but profile creation succeeded
                  if (response.fineTuningFailed) {
                    // Show alert to the user
                    setTimeout(() => {
                      alert(response.error || "Character profile was created, but fine-tuning failed.");
                    }, 500); // Small delay to ensure UI updates first
                  }
                }
              });
            }
          }
        } else {
          console.error('Failed to create chat message:', response.error);
          // Restore input message if there was an error
          setInputMessage(currentInputMessage);
        }
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleManualDeusResponse = () => {
    if (activeMind) {
      setIsTyping(true);

      sendWithAuth('getInterviewerResponse', { 
        mindId: activeMind, 
        selectedInterviewers: selectedInterviewers,
        pivotConversation: true
      }, (response) => {
        setIsTyping(false);

        if (response.success) {
          const aiMessage = {
            message: response.aiMessage,
            is_user: false,
            timestamp: new Date()
          };
          setMessages(prevMessages => [...prevMessages, aiMessage]);

          // Store the AI message in the database
          const event = isAIConversation ? 'createAIChatMessage' : 'createChatMessage';
          sendWithAuth(event, {
            userId: user._id,
            mindId: activeMind,
            message: response.aiMessage,
            model: response.model,
            isUser: false
          }, (storeResponse) => {
            if (!storeResponse.success) {
              console.error('Failed to store AI message:', storeResponse.error);
            }
          });
        } else {
          console.error('Failed to get AI response:', response.error);
        }
      });
    }
  };

  useEffect(() => {
    const handleMouseDown = (e) => {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
      const newWidth = e.clientX - sidebarRef.current.getBoundingClientRect().left;
      const maxWidth = window.innerWidth * 0.5; // 50% of window width
      if (newWidth >= 150 && newWidth <= maxWidth) {
        sidebarRef.current.style.width = `${newWidth}px`;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleResize = () => {
      const currentWidth = parseInt(sidebarRef.current.style.width, 10);
      const maxWidth = window.innerWidth * 0.5; // 50% of window width
      if (currentWidth > maxWidth) {
        sidebarRef.current.style.width = `${maxWidth}px`;
      }
    };

    const resizerElement = resizerRef.current;
    resizerElement.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('resize', handleResize);

    return () => {
      resizerElement.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Function to scroll to the bottom of the messages container
  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto'; // Reset the height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set it to the scroll height
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  // Scroll to bottom when messages change or typing starts
  useEffect(() => {
    // Scroll immediately for immediate feedback
    scrollToBottom();
    
    // Also scroll after a short delay to ensure all content is rendered
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [messages, inputMessage, activeChatId, isChatLoading, isTyping]);

  const handleManageProfiles = () => {
    setIsManageProfilesOpen(true);
  };

  const handleMindSelect = (id) => {
    // If selecting the same mind, do nothing
    if (id === activeMind) {
      return;
    }
    
    // Cancel any pending request
    currentRequestIdRef.current = null;
    
    // Clear messages immediately to avoid showing wrong mind's messages
    setMessages([]);
    
    // Set loading state
    setIsChatLoading(true);
    
    // Remove any profile creation notification when switching minds
    const notification = document.getElementById('profile-creation-notification');
    if (notification) {
      notification.remove();
    }
    
    const selectedMind = minds.find(mind => mind.id === id);
    const hasProfile = selectedMind?.default_profile != null;
    
    // First, set the basic mind data and clear chat ID
    setActiveMind(id);
    setActiveProfile(selectedMind?.default_profile || null);
    
    // Reset activeChatId to avoid any stale references
    setActiveChatId(null);
    
    // Determine if we should switch to AI mode
    const shouldUseAIMode = hasProfile;
    
    // If we should use AI mode and there are chats, select the first chat
    if (shouldUseAIMode && selectedMind?.chats?.length > 0) {
      // Set AI conversation mode
      setIsAIConversation(true);
      
      // Get the first chat ID
      const firstChatId = selectedMind.chats[0].id;
      
      // Update the active chat ID - this will trigger the message fetch useEffect
      setActiveChatId(firstChatId);
    } else if (shouldUseAIMode) {
      // If we should use AI mode but there are no chats, create a new one
      setIsAIConversation(true);
      
      sendWithAuth('createAIChat', {
        userId: user._id,
        mindId: id
      }, (response) => {
        if (response.success) {
          // Extract the new chat ID
          const newChatId = response.chatId || response.chat.id;
          
          // Locally update the minds state with the new chat
          setMinds(prevMinds => prevMinds.map(mind => {
            if (mind.id === id) {
              return {
                ...mind,
                chats: [response.chat, ...(mind.chats || [])]
              };
            }
            return mind;
          }));
          
          // Set the new chat as active
          setActiveChatId(newChatId);
        } else {
          console.error('Failed to create new chat:', response.error);
          setIsChatLoading(false);
        }
      });
    } else {
      // Otherwise, clear chat ID and set conversation mode appropriately
      setActiveChatId(null);
      setIsAIConversation(shouldUseAIMode);
      setIsChatLoading(false);
    }
  }
  
  const handleChatSelect = (chatId) => {
    // If selecting the same chat, do nothing
    if (chatId === activeChatId) {
      return;
    }
    
    // Cancel any pending request
    currentRequestIdRef.current = null;
    
    // Clear messages immediately to avoid showing wrong chat's messages
    setMessages([]);
    
    // Set loading state
    setIsChatLoading(true);
    
    // Update the active chat ID - this will trigger the message fetch useEffect
    setActiveChatId(chatId);
  }

  const createNewChat = () => {
    if (!activeMind) return;
    
    // Show loading indicator if needed
    setIsChatLoading(true);
    
    sendWithAuth('createAIChat', {
      userId: user._id,
      mindId: activeMind
    }, (response) => {
      setIsChatLoading(false);
      
      if (response.success) {
        // First clear messages to avoid any visual confusion
        setMessages([]);
        
        // Extract the new chat ID
        const newChatId = response.chatId || response.chat.id;
        
        // Locally update the minds state with the new chat at the beginning
        setMinds(prevMinds => prevMinds.map(mind => {
          if (mind.id === activeMind) {
            return {
              ...mind,
              chats: [response.chat, ...(mind.chats || [])]
            };
          }
          return mind;
        }));
        
        // Set the new chat as active - this will trigger the message fetch useEffect
        setActiveChatId(newChatId);  
      } else {
        console.error('Failed to create new chat:', response.error);
      }
    });
  }

  const handleSortChange = (event) => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    
    // Fetch minds with new sort
    sendWithAuth('getUserMinds', { 
      userId: user._id,
      sortBy: newSortBy
    });
  };

  const toggleFavorite = (e, mindId) => {
    e.stopPropagation(); // Prevent triggering the mind selection
    
    const mind = minds.find(m => m.id === mindId);
    if (!mind) return;
    
    const newFavoriteStatus = !mind.favorite;
    
    // Optimistically update the UI
    const updatedMind = { ...mind, favorite: newFavoriteStatus };
    setMinds(prevMinds => {
      const updatedMinds = prevMinds.map(m => 
        m.id === mindId ? updatedMind : m
      );
      
      // If we're sorting by favorites, reorder the list locally
      if (sortBy === 'favorites') {
        return [
          ...updatedMinds.filter(m => m.favorite).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
          ...updatedMinds.filter(m => !m.favorite).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        ];
      }
      
      return updatedMinds;
    });
    
    // Send the update to the server
    sendWithAuth('updateMind', {
      mindId,
      favorite: newFavoriteStatus
    });
  };

  const startEditingMindName = (e, mindId) => {
    e.stopPropagation(); // Prevent triggering the mind selection
    
    const mind = minds.find(m => m.id === mindId);
    if (!mind) return;
    
    setEditingMindId(mindId);
    setEditingMindName(mind.name);
    // Don't set expandedMindId when editing the name
  };

  const saveEditedMindName = (e) => {
    e.preventDefault();
    
    if (!editingMindId || !editingMindName.trim()) {
      setEditingMindId(null);
      return;
    }

    const tempMindName = editingMindName.trim();

    // Add validation to prevent spaces
    if (tempMindName.includes(' ')) {
      alert('Mind name cannot contain spaces');
      return;
    }
    
    sendWithAuth('updateMind', {
      mindId: editingMindId,
      name: tempMindName
    });
    
    // Don't keep the mind expanded after saving
    setEditingMindId(null);
  };

  const handleMindNameInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      setEditingMindId(null);
    } else if (e.key === 'Enter') {
      saveEditedMindName(e);
    } else if (e.key === ' ') {
      // Prevent space key from being entered
      e.preventDefault();
    }
  };

  const startEditingChatName = (e, chatId, currentName) => {
    e.stopPropagation(); // Prevent triggering the chat selection
    setEditingChatId(chatId);
    setEditingChatName(currentName || `Chat ${new Date().toLocaleDateString()}`);
  };

  const saveEditedChatName = (e) => {
    e.preventDefault();
    
    if (!editingChatId || !editingChatName.trim()) {
      setEditingChatId(null);
      return;
    }
    
    sendWithAuth('updateChatName', {
      chatId: editingChatId,
      title: editingChatName.trim()
    }, (response) => {
      if (response.success) {
        // Update the chat name locally instead of refreshing all minds
        setMinds(prevMinds => prevMinds.map(mind => ({
          ...mind,
          chats: mind.chats?.map(chat => 
            chat.id === editingChatId 
              ? { ...chat, title: editingChatName.trim() }
              : chat
          )
        })));
      } else {
        console.error('Failed to update chat name:', response.error);
      }
      
      setEditingChatId(null);
    });
  };

  const handleChatNameInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      setEditingChatId(null);
    } else if (e.key === 'Enter') {
      saveEditedChatName(e);
    }
  };

  const deleteChat = (e, chatId) => {
    e.stopPropagation(); // Prevent triggering the chat selection
    
    if (!window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      return;
    }
    
    sendWithAuth('deleteChat', { chatId }, (response) => {
      if (response.success) {
        // Update minds state to remove the deleted chat
        setMinds(prevMinds => prevMinds.map(mind => {
          if (mind.id === activeMind) {
            return {
              ...mind,
              chats: mind.chats.filter(chat => chat.id !== chatId)
            };
          }
          return mind;
        }));
        
        // If the deleted chat was active, select another chat
        if (activeChatId === chatId) {
          const currentMind = minds.find(mind => mind.id === activeMind);
          const remainingChats = currentMind.chats.filter(chat => chat.id !== chatId);
          if (remainingChats.length > 0) {
            setActiveChatId(remainingChats[0].id);
          } else {
            setActiveChatId(null);
          }
        }
      } else {
        console.error('Failed to delete chat:', response.error);
      }
    });
  };

  // Fetch messages when mind or chat changes
  useEffect(() => {
    // Skip if we don't have an active mind or if we're in AI mode but don't have a chat ID
    if (!activeMind || (isAIConversation && !activeChatId)) {
      return;
    }
    
    // Set loading state
    setIsChatLoading(true);
    setMessages([]); // Clear messages while loading
    
    // Track the current request to handle race conditions
    const requestId = Date.now();
    currentRequestIdRef.current = requestId;
    
    // Determine which API to call based on conversation mode
    const event = isAIConversation ? 'getAIChatHistory' : 'getTrainingMessages';
    const payload = isAIConversation 
      ? { mindId: activeMind, chatId: activeChatId } 
      : { mindId: activeMind };
    
      sendWithAuth(event, payload, (response) => {
      // Skip processing if this is a stale response
      if (currentRequestIdRef.current !== requestId) {
        return;
      }
      
      if (response.success) {
        const messages = response.conversationHistory || response.trainingMessages;
        setMessages(messages);

        // Update training progress based on loaded messages
        if (!isAIConversation) {
          const userMessageCount = messages.filter(msg => msg.is_user).length;
          setTrainingProgress(prev => ({
            ...prev,
            [activeMind]: userMessageCount
          }));
        }
      
        // Case 1: Check if there are no messages
        const noMessages = !messages || messages.length === 0;
        
        // Case 2: Check if the last message is from the user
        const lastMessageIsFromUser = messages && 
                                      messages.length > 0 && 
                                      messages[messages.length - 1].is_user === true;
        
        if (noMessages || lastMessageIsFromUser) {
          // Start typing animation
          setIsTyping(true);
          // Route to the appropriate response getter based on conversation mode
          setTimeout(() => {
            if (isAIConversation) {
              getAgentResponse(activeProfile, activeChatId);
            } else {
              getInterviewerResponse();
            }
          }, 300);
        }
      } else {
        console.error(`Failed to retrieve ${isAIConversation ? 'chat' : 'training'} messages:`, response.error);
      }
      
      // Clear loading states
      setIsChatLoading(false);
    });
    
    // Cleanup function
    return () => {
      currentRequestIdRef.current = null;
    };
  }, [activeMind, activeChatId, isAIConversation, activeProfile, getAgentResponse, getInterviewerResponse]);
  
  useEffect(() => {
    if (user) {
      sendWithAuth('getUserMinds', { 
        userId: user._id,
        sortBy: 'favorites' // Default sort for initial fetch
      });
    }
  }, [user, deusSocket]);

  return (
    <div style={{ position: 'relative', height: '100vh' }}>
      <div className="chat-container">
        <div className="chat-sidebar" ref={sidebarRef}>
          <div className="minds-header">
            <div className="minds-header-top">
              <h2>Mind Clones</h2>
              <button 
                onClick={() => setIsCreateMindOpen(true)} 
                disabled={minds.length > 0 && user?.membership !== 'pro'}
                title={minds.length > 0 && user?.membership !== 'pro' ? 
                  "Free accounts are limited to 1 mind clone. Upgrade to Pro for unlimited minds." : 
                  "Create new mind clone"}
                className={minds.length > 0 && user?.membership !== 'pro' ? "disabled" : ""}
              >
                +
              </button>
            </div>
            <div className="sort-container">
              <select 
                value={sortBy} 
                onChange={handleSortChange}
                className="minds-sort-select"
              >
                <option value="favorites">Favorites</option>
                <option value="updated_at_desc">Recently Updated</option>
                <option value="created_at_desc">Newest Minds</option>
                <option value="created_at_asc">Oldest Minds</option>
              </select>
              <span className="sort-icon">⇅</span>
            </div>
          </div>
          <div className="minds-list">
            {minds.map(mind => (
              <div key={mind.id} className="mind-container">
                <div 
                  className={`mind-item ${activeMind === mind.id ? 'active' : ''}`}
                  onClick={() => handleMindSelect(mind.id)}
                >
                  <div className="mind-item-content">
                    {editingMindId === mind.id ? (
                      <form onSubmit={saveEditedMindName} className="edit-mind-form">
                        <input
                          type="text"
                          value={editingMindName}
                          onChange={(e) => setEditingMindName(e.target.value)}
                          onKeyDown={handleMindNameInputKeyDown}
                          onBlur={saveEditedMindName}
                          autoFocus
                          className="edit-mind-input"
                        />
                      </form>
                    ) :
                      <>
                        <div className="mind-name-container">
                          <span 
                            className="mind-name"
                            onDoubleClick={(e) => startEditingMindName(e, mind.id)}
                          >
                            {mind.name}
                          </span>
                          <button 
                            className="edit-mind-button"
                            onClick={(e) => startEditingMindName(e, mind.id)}
                            aria-label="Edit mind name"
                          >
                            ✎
                          </button>
                        </div>
                        <div className="mind-actions">
                          <button 
                            className={`favorite-button ${mind.favorite ? 'favorited' : ''}`}
                            onClick={(e) => toggleFavorite(e, mind.id)}
                            aria-label={mind.favorite ? "Remove from favorites" : "Add to favorites"}
                          >
                            {mind.favorite ? '★' : '☆'}
                          </button>
                        </div>
                      </>
                    }
                  </div>
                </div>
                
                {/* Only show chats for the active mind if it has chats and we're in AI conversation mode */}
                {activeMind === mind.id && isAIConversation && (
                  <div className="chats-list">
                    {/* New Chat button at the top */}
                    <button 
                      className="new-chat-button top"
                      onClick={(e) => {
                        e.stopPropagation();
                        createNewChat();
                      }}
                    >
                      New Chat
                    </button>
                    
                    {mind.chats && mind.chats.length > 0 ? (
                      mind.chats.map(chat => (
                        <div 
                          key={chat.id} 
                          className={`chat-item ${activeChatId === chat.id ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChatSelect(chat.id);
                          }}
                        >
                          <span className="chat-title">
                            {editingChatId === chat.id ? (
                              <form onSubmit={saveEditedChatName} className="edit-chat-form">
                                <input
                                  type="text"
                                  value={editingChatName}
                                  onChange={(e) => setEditingChatName(e.target.value)}
                                  onKeyDown={handleChatNameInputKeyDown}
                                  onBlur={saveEditedChatName}
                                  autoFocus
                                  className="edit-chat-input"
                                />
                              </form>
                            ) : (
                              chat.title || `Chat ${new Date(chat.created_at).toLocaleDateString()}`
                            )}
                          </span>
                          {!editingChatId && (
                            <>
                              <button 
                                className="edit-chat-button"
                                onClick={(e) => startEditingChatName(e, chat.id, chat.title || `Chat ${new Date(chat.created_at).toLocaleDateString()}`)}
                                aria-label="Edit chat name"
                              >
                                ✎
                              </button>
                              <button 
                                className="delete-chat-button"
                                onClick={(e) => deleteChat(e, chat.id)}
                                aria-label="Delete chat"
                              >
                                ⌦
                              </button>
                            </>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="no-chats-message">No chats yet</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="resizer" ref={resizerRef} />
        <div className="chat-main">
          <div className="chat-header">
            <div className="chat-header-left">
              <h2 className="mind-title">
                {activeMind ? (
                  <>
                    {mindName}
                    {activeMind && (
                      <span className="profile-badge" onClick={handleManageProfiles}>
                        {activeProfile && `v${activeProfile} `}
                        <div className="settings-icon-wrapper">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                          </svg>
                        </div>
                      </span>
                    )}
                  </>
                ) : (
                  'Select a Mind Clone'
                )}
              </h2>
            </div>
            <div className="chat-header-right">
              <div className="toggle-container">
                <span>Interview</span>
                <label className={`switch ${isTyping ? 'disabled' : ''}`}>
                  <input 
                    type="checkbox" 
                    checked={isAIConversation} 
                    onChange={toggleConversationMode}
                    disabled={isTyping}
                  />
                  <span className="slider round"></span>
                </label>
                <span>Chat</span>
              </div>
              <button 
                className={`copy-conversation-icon ${messages.length === 0 ? 'disabled' : ''}`}
                onClick={copyConversation}
                title={messages.length > 0 ? "Copy conversation" : "No messages to copy"}
                disabled={messages.length === 0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
            </div>
          </div>
          
          {(isLoading || isChatLoading) ? (
            <div style={{ height: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LoadingSpinner />
            </div>
          ) : isAIConversation && !activeChatId && activeChats.length > 0 ? (
            <div className="select-chat-message">
              <p>Please select a chat from the sidebar to start a conversation.</p>
            </div>
          ) : (
            <>
              <div className="messages-container">
                {messages.map((message, index) => (
                  <div 
                    key={index} 
                    className={`message ${message.is_user ? 'user-message' : 'agent-message'}`}
                  >
                    <div className="message-content">{message.message}</div>
                    <div className="message-timestamp">
                      <div className="message-timestamp-left">
                        {message.profile_version && (
                          <span className="profile-badge">v{message.profile_version}</span>
                        )}
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </div>
                      <div className="message-actions">
                        {!message.is_user && index === messages.length - 1 && !isAIConversation && !isTyping && (
                          <button 
                            onClick={handleManualDeusResponse} 
                            className="command-button"
                          >
                            Skip
                          </button>
                        )}
                        <button 
                          className={`copy-message-button ${copiedMessageIds[message.timestamp.toString()] ? 'copy-success' : ''}`}
                          onClick={(e) => copyMessage(message, e)}
                          title="Copy this message"
                        >
                          {copiedMessageIds[message.timestamp.toString()] ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="message agent-message typing-indicator">
                    <div className="message-content">
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                    </div>
                  </div>
                )}
              </div>
              {/* Only render input container if we have an active mind AND either we're not in AI mode OR we have an active chat */}
              {activeMind && (!isAIConversation || (isAIConversation && activeChatId)) && (
                <div className="chat-input-container">
                  {!isAIConversation && (
                    <div className="interviewer-selector-container">
                      <InterviewerSelector
                        value={selectedInterviewers}
                        onChange={setSelectedInterviewers}
                        disabled={isTyping}
                      />
                    </div>
                  )}
                  <div className="chat-input-area">
                    <form onSubmit={handleSendMessage} className="input-form">
                      <textarea
                        ref={textareaRef}
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={!isAIConversation ? `Answering as ${mindName}...` : `Message ${mindName}...`}
                        className="chat-input"
                        rows="1"
                      />
                    </form>
                  </div>
                  {!isAIConversation && !isLoading && !isChatLoading && (
                    <TrainingProgress 
                      progress={trainingProgress[activeMind] || 0}
                      isVisible={
                        minds.length > 0 && 
                        activeMind && 
                        minds.find(mind => mind.id === activeMind) && 
                        !isAIConversation
                      }
                      isChatLoading={isChatLoading}
                      mindId={activeMind}
                      setMinds={setMinds}
                      setActiveProfile={setActiveProfile}
                      isCreatingProfile={isCreatingProfile}
                      setIsCreatingProfile={setIsCreatingProfile}
                      onStartChat={() => {
                        setIsAIConversation(true);
                        createNewChat();
                      }}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      <CreateMindModal 
        isOpen={isCreateMindOpen} 
        onClose={() => setIsCreateMindOpen(false)} 
        isCreateMindLoading={isCreateMindLoading} 
        setIsCreateMindLoading={setIsCreateMindLoading} 
      />
      <MindSettingsModal 
        isOpen={isManageProfilesOpen} 
        onClose={() => setIsManageProfilesOpen(false)} 
        activeMind={activeMind}
        activeProfile={activeProfile}
        setActiveProfile={setActiveProfile}
      />
      <WelcomeModal
        isOpen={isWelcomeModalOpen}
        onClose={() => setIsWelcomeModalOpen(false)}
        onCreateMind={() => {
          setIsWelcomeModalOpen(false);
          setIsCreateMindOpen(true);
        }}
      />    
    </div>
  );
}

export default Main;